import React, { useState } from 'react';
import {
  Trash2,
  Share2,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import {
  Bar,
  Line,
  Pie,
  Radar
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Tooltip,
  Legend
);

// Mock favorite reports
const initialFavorites = [
  {
    id: 'fav_1',
    title: 'Daily Operations',
    chartType: 'bar',
    prompt: 'Show me daily operational summary for all departments.',
    description: 'Auto-saved report: Daily Operations',
    savedOn: '2025-05-21 10:40',
    updatedOn: '2025-05-24 06:40',
    sharedWith: null
  },
  {
    id: 'fav_2',
    title: 'Weekly Performance',
    chartType: 'line',
    prompt: 'Generate weekly performance trends by region.',
    description: 'Auto-saved report: Weekly Performance',
    savedOn: '2025-05-13 10:40',
    updatedOn: '2025-05-13 16:40',
    sharedWith: 'Taylor'
  },
  {
    id: 'fav_3',
    title: 'System Health Overview',
    chartType: 'pie',
    prompt: 'Give me system uptime and health metrics.',
    description: 'Auto-saved report: System Health Overview',
    savedOn: '2025-05-06 10:40',
    updatedOn: '2025-05-09 05:40',
    sharedWith: null
  },
  {
    id: 'fav_4',
    title: 'Resource Forecast',
    chartType: 'forecast',
    prompt: 'Forecast resource utilization for next month.',
    description: 'Auto-saved report: Resource Forecast',
    savedOn: '2025-05-19 10:40',
    updatedOn: '2025-05-22 09:40',
    sharedWith: 'Alex'
  },
  {
    id: 'fav_5',
    title: 'Risk Heatmap',
    chartType: 'radar',
    prompt: 'Visualize risk levels across verticals.',
    description: 'Auto-saved report: Risk Heatmap',
    savedOn: '2025-05-14 10:40',
    updatedOn: '2025-05-17 09:40',
    sharedWith: null
  },
  {
    id: 'fav_6',
    title: 'Customer Churn Trend',
    chartType: 'area',
    prompt: 'Predict customer churn over last quarter.',
    description: 'Auto-saved report: Customer Churn Trend',
    savedOn: '2025-05-01 10:40',
    updatedOn: '2025-05-03 08:40',
    sharedWith: 'Morgan'
  }
];

// Define chart previews
const getChartPreview = (type: string) => {
  switch (type) {
    case 'bar':
      return (
        <Bar
          data={{
            labels: ['Team A', 'Team B', 'Team C'],
            datasets: [{ label: 'Tasks', data: [12, 19, 14], backgroundColor: '#4ade80' }]
          }}
          options={{
            plugins: { legend: { labels: { color: 'white' } } },
            scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } }
          }}
        />
      );
    case 'line':
      return (
        <Line
          data={{
            labels: ['Week 1', 'Week 2', 'Week 3'],
            datasets: [{ label: 'Velocity', data: [30, 40, 35], borderColor: '#60a5fa', tension: 0.4 }]
          }}
          options={{
            plugins: { legend: { labels: { color: 'white' } } },
            scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } }
          }}
        />
      );
    case 'area':
    case 'forecast':
      return (
        <Line
          data={{
            labels: ['Jan', 'Feb', 'Mar', 'Apr'],
            datasets: [{
              label: 'Churn Forecast',
              data: [120, 140, 110, 150],
              fill: true,
              borderColor: '#fbbf24',
              backgroundColor: 'rgba(251,191,36,0.2)',
              tension: 0.4
            }]
          }}
          options={{
            plugins: { legend: { labels: { color: 'white' } } },
            scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } }
          }}
        />
      );
    case 'pie':
      return (
        <Pie
          data={{
            labels: ['Uptime', 'Downtime'],
            datasets: [{ data: [95, 5], backgroundColor: ['#34d399', '#f87171'] }]
          }}
          options={{ plugins: { legend: { labels: { color: 'white' } } } }}
        />
      );
    case 'radar':
      return (
        <Radar
          data={{
            labels: ['Compliance', 'Security', 'Delivery', 'Latency'],
            datasets: [{
              label: 'Risk Score',
              data: [80, 65, 70, 75],
              backgroundColor: 'rgba(167,139,250,0.2)',
              borderColor: '#a78bfa'
            }]
          }}
          options={{
            plugins: { legend: { labels: { color: 'white' } } },
            scales: {
              r: {
                pointLabels: { color: 'white' },
                grid: { color: '#4b5563' },
                ticks: { display: false }
              }
            }
          }}
        />
      );
    default:
      return <div className="text-sm text-gray-400">Chart preview unavailable</div>;
  }
};

const FavoritesPanel = () => {
  const [favorites, setFavorites] = useState(initialFavorites);
  const [activeExpanded, setActiveExpanded] = useState<string | null>(null);

  const deleteFavorite = (id: string) => {
    setFavorites((prev) => prev.filter((f) => f.id !== id));
  };

  const expandedChart = favorites.find((f) => f.id === activeExpanded);

  return (
    <div className="p-6 text-white relative">
      <h2 className="text-xl font-semibold text-indigo-400 mb-6">📌 Favorite Reports</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {favorites.map((fav) => (
          <div key={fav.id} className="bg-neutral-900 border border-neutral-800 rounded-md p-4 shadow hover:shadow-xl transition-all">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-md font-semibold">{fav.title}</h3>
                <p className="text-xs text-gray-500">{fav.description}</p>
              </div>
              <div className="flex gap-2 items-center">
                <button onClick={() => setActiveExpanded(fav.id)}>
                  <Maximize2 className="w-4 h-4 text-gray-400 hover:text-white"  />
                </button>
                <Share2 className="w-4 h-4 text-gray-400 hover:text-white cursor-pointer" />
                <button onClick={() => deleteFavorite(fav.id)} title="Delete">
                  <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-300 italic mb-3">💬 {fav.prompt}</div>
            <div className="h-40 bg-neutral-800 p-2 rounded-md overflow-hidden">
              {getChartPreview(fav.chartType)}
            </div>
            <div className="text-xs text-gray-500 mt-3 flex flex-col gap-1">
              <span>🕓 Saved: {fav.savedOn}</span>
              <span>🔁 Updated: {fav.updatedOn}</span>
              {fav.sharedWith && <span>📤 Shared with: {fav.sharedWith}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Fullscreen Chart Overlay */}
      {activeExpanded && expandedChart && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center p-6 overflow-auto">
          <div className="w-full max-w-6xl bg-neutral-900 border border-neutral-800 p-6 rounded-lg shadow-lg relative">
            <button
              onClick={() => setActiveExpanded(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              title="Minimize"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-lg font-semibold text-white mb-1">{expandedChart.title}</h3>
            <p className="text-sm text-gray-400 italic mb-4">💬 {expandedChart.prompt}</p>
            <div className="h-[400px] bg-neutral-800 p-4 rounded">{getChartPreview(expandedChart.chartType)}</div>
            <div className="text-xs text-gray-500 mt-4 flex justify-between">
              <div>
                <span>🕓 Saved: {expandedChart.savedOn}</span> • <span>🔁 Updated: {expandedChart.updatedOn}</span>
              </div>
              {expandedChart.sharedWith && <span>📤 Shared with: {expandedChart.sharedWith}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesPanel;
